"use client"
import React, {useContext, createContext, useEffect, useState, useCallback, useMemo, useRef} from 'react';

import {useLocalStorage} from "react-use";
import {getQueryParam, isDev, removeQueryParam, showConfetti} from "@/components/DownloaderApp/utils";
import {Button} from "@heroui/button";
import {ModalBody, ModalFooter, ModalHeader} from "@heroui/modal";
import {useMonitoring} from "@/components/monitoring";
import {useModal} from "@/components/DownloaderApp/Modal";
import {showUpsellModal} from "@/components/DownloaderApp/UpsellPopup";
import {Bold} from "@/components/DownloaderApp/Bold";
import {showTwitchUpsellModal} from "@/components/DownloaderApp/TwitchUpsellPopup";
import {VerifiedIcon} from "@/components/DownloaderApp/icons";
import {PremiumManagePopup, type SubscriptionDetails} from "@/components/DownloaderApp/PremiumManagePopup";
import {getLicenseExpiredMessage, isLicenseExpired} from "@/utils/license-expiration";
import {saveLicenseKeyToFile} from "@/utils/save-license-key-to-file";

export type PremiumSubscriptionDetails = SubscriptionDetails & {
    status: string;
    maxVods?: number;
};

export function getLicenseKey(): string | null {
    try {
        return JSON.parse(localStorage.getItem("license_key"))
    } catch (err) {
        return null;
    }
}

const color = {
    style: {color: '#51fc1a'}
}

async function fetchLicenseInfo(licenseKey: string | null): Promise<PremiumSubscriptionDetails> {
    console.log({isDev})
    if (isDev) {
        const expiresAt = "2026-02-10T11:04:35.000000Z";
        return {
            "email": "adasq3+q@gmail.com",
            expiresAt,
            "env": "production",
            status: isLicenseExpired({expiresAt}) ? "expired" : "active",
            "platform": "kick",
            "maxVods": 1333,
            "vods": [
                "2fded581-f0a9-44b3-a931-7164a15b928c",
                "fd323bff-3133-493f-a91c-71fc71d34082",
                "ba944b4b-5aa4-4e2a-ac2f-efab095c93bf"
            ]
        }
    }
    try {
        const result = await fetch(`/api/subscription?license_key=${licenseKey}`);
        if (result.ok) {
            return await result.json();
        }
    } catch (err) {
        console.log(err);
    }

    return {
        status: "inactive",
    }

}

type PremiumManagerProps = {
    hideCta?: boolean;
    preview?: Pick<PremiumContextType, "licenseKey" | "setLicenseKey" | "details">;
};

export function PremiumManager({hideCta, preview}: PremiumManagerProps) {
    const premium = usePremium();
    const {licenseKey, setLicenseKey, details} = preview ?? premium;
    const {show} = useModal();

    function showUpsell(data = {}) {
        if (preview) return;
        if(details?.platform === "twitch") {
            showTwitchUpsellModal(show, data)
        }else {
            showUpsellModal(show, data)
        }
    }

    async function onManageClicked() {
        const {accepted} = await show({
            component: PremiumManagePopup,
            data: {
                licenseKey, setLicenseKey, details, isPreview: !!preview, size: "5xl"
            }
        });

        if (accepted) {
            showUpsell({
                source: 'license-manager-modal'
            })
        }
    }


    let licenseAppliedSection = <span>License <span {...color} className={""}>{minifyLicenseKey(licenseKey)}</span> applied. </span>
    licenseAppliedSection = <><span {...color} className={"inline-flex items-center gap-1 align-middle"}><VerifiedIcon className={"text-xl"}/> License applied</span></>

    const manageCta = hideCta ? null : <Button
        onPress={onManageClicked}
        color={"default"}
        variant={"bordered"}
        className={"ml-2 h-6 min-w-0 px-2 text-xs"}
        size={"sm"}>
        Manage
    </Button>

    const getMoreCta = hideCta ? null : <Button
        isDisabled={!!preview}
        onPress={() => showUpsell({
            source: 'license-banner'
        })}
        color="primary"
        className={"ml-2 h-6 min-w-0 px-2 text-xs"}
        size={"sm"}>
        Get More Credits
    </Button>

    if (details === undefined) {
        return null;
    }

    if (!details) {
        return (<div key={"applied"} className={"text-sm text-center animate-fade-in-up"}>
            {licenseAppliedSection}
            {manageCta}
        </div>)
    }

    function timeLeftHuman(targetTime) {
        const now = Date.now();
        let diffMs = targetTime - now;

        if (diffMs <= 0) return "expired";

        const seconds = Math.floor(diffMs / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        const monthsTotal = Math.floor(days / 30); // UX-friendly month
        const years = Math.floor(monthsTotal / 12);
        const months = monthsTotal % 12;

        if (seconds < 10) return "expires now";
        if (seconds < 60) return `expires in ${seconds} seconds`;
        if (minutes === 1) return "expires in about a minute";
        if (minutes < 60) return `expires in ${minutes} minutes`;
        if (hours === 1) return "expires in about an hour";
        if (hours < 24) return `expires in ${hours} hours`;
        if (days === 1) return "expires in 1 day";
        if (days < 30) return `expires in ${days} days`;


        const formatted = targetTime.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

        return `expires on ${formatted}`;

    }

    if (details.status === "expired") {
        const [prefix, suffix] = getLicenseExpiredMessage(details.expiresAt).split("expired");

        return (
            <div key="expired" className="flex flex-wrap items-center justify-center gap-1 text-sm text-center animate-fade-in-up">
                <span role="status" className="inline-flex items-center gap-1 text-warning">
                    <span aria-hidden="true">⚠️</span>
                    <span>{prefix}<b className="underline underline-offset-4">expired</b>{suffix}</span>
                </span>
                {getMoreCta}
                {manageCta}
            </div>
        );
    }

    if (details.status !== "active") {
        return (
            <div key="inactive" className="flex flex-wrap items-center justify-center gap-1 text-sm text-center animate-fade-in-up">
                <span role="status" className="inline-flex items-center gap-1 text-warning">
                    <span aria-hidden="true">⚠️</span>
                    License is <b className="underline underline-offset-4">inactive</b>
                </span>
                {manageCta}
            </div>
        );
    }

    let expiresAt;
    try {
        if (!details.expiresAt) throw new Error('No expiresAt in license details');
        expiresAt = timeLeftHuman(new Date(details.expiresAt));
    } catch (e) {
        console.error(e);
    }

    if (typeof details.maxVods !== "number" || !Array.isArray(details.vods)) {
        console.error("Active subscription is missing download-credit details");
        return (
            <div role="alert" className="text-sm text-center text-danger">
                Subscription details could not be loaded.
                {manageCta}
            </div>
        );
    }

    const downloadsLeft = details.maxVods - details.vods.length;

    let downloadsLeftSection = <span><span {...color}>{downloadsLeft} </span>
        credit{downloadsLeft > 1 ? "s" : ""} left</span>;
    if (downloadsLeft === 0) {
        downloadsLeftSection = (
            <span role="status" className="inline-flex items-center gap-1 text-warning">
                <span aria-hidden="true">⚠️</span>
                No credits left
            </span>
        );
    }

    return (<div key={"active"} className={"flex flex-wrap items-center justify-center gap-x-1 text-sm text-center animate-fade-in-up"}>
        {downloadsLeft !== 0 && <>
            {licenseAppliedSection}
            <span aria-hidden="true">⸱</span>
        </>}
        {downloadsLeftSection}
        {downloadsLeft !== 0 && <>
            <span aria-hidden="true">⸱</span>
            <span>{expiresAt}</span>
        </>}
        {downloadsLeft <= 0 && getMoreCta}
        {manageCta}
    </div>)
}

function minifyLicenseKey(licenseKey) {
    try {
        return `XXXX-${licenseKey.split('-').reverse()[0]}`
    } catch (e) {
        return licenseKey;
    }
}


export function LicenseAppliedPopup({data, onClose}) {
    const {track, updateUser} = useMonitoring()
    const buttonRef = useRef<HTMLButtonElement>(null)
    useEffect(() => {
        showConfetti({
            ticks: 110
        })
        track("[Premium] License applied", {licenseKey: data.licenseKey, fromCheckout: data.fromCheckout})
        updateUser({...data})
        try {
            window.posthog.alias(data.licenseKey, window.posthog.get_distinct_id())
        } catch (err) {
        }
        try {
            window.posthog.alias(data.email, window.posthog.get_distinct_id())
        } catch (err) {
        }
        // Focus button after modal animation completes
        setTimeout(() => buttonRef.current?.focus(), 100)
    }, []);
    return (
        <>
            <ModalHeader className="flex flex-col gap-1 text-center pt-6">
                <span className="text-4xl mb-2">🎉</span>
                <span id="modal-title" className="text-xl font-semibold">
                    You're All Set!
                </span>
            </ModalHeader>
            <ModalBody className="text-center pb-2">
                <p className="text-default-600 mb-3">
                    {data.fromCheckout
                        ? "Thank you for your purchase! Your download credits are now available."
                        : "Your license has been applied."}
                </p>
                <div className="bg-default-100 rounded-lg px-4 py-3 inline-block">
                    <span className="text-xs text-default-500 block mb-1">License Key</span>
                    <span className="font-mono text-sm" {...color}>{data.licenseKey}</span>
                </div>
                {data.fromCheckout && (
                    <><p className="text-sm text-default-500 mt-4">
                        A confirmation email with your license has been sent to your email.
                    </p>
                        <p className="text-sm text-default-500 mt-1">
                            Don't see it? Check your <Bold>spam folder</Bold>.
                        </p></>
                )}
            </ModalBody>
            <ModalFooter className="justify-center pb-6 flex-col sm:flex-row gap-2">
                {data.fromCheckout && (
                    <Button
                        color="default"
                        variant="bordered"
                        size="lg"
                        onPress={() => {
                            saveLicenseKeyToFile(data.licenseKey)
                            track("[Premium] License key saved to file", {licenseKey: data.licenseKey})
                        }}
                    >
                        Save License Key to File
                    </Button>
                )}
                <Button ref={buttonRef} color="primary" size="lg" onPress={onClose}>
                    Start Downloading
                </Button>
            </ModalFooter>
        </>
    );
}

type  childrenType = {
    children: React.ReactNode;
}

type PremiumContextType = {
    details: PremiumSubscriptionDetails | null | undefined
    hasPremium: boolean
    licenseKey: string | null
    setLicenseKey: (l: string | null) => void
    refresh: (l?: string | null) => void
}


const PremiumContext = createContext<PremiumContextType | null>(null)

export const PremiumProvider = ({children}: childrenType) => {
    const [licenseKey, setLicenseKey] = useLocalStorage<string | null>("license_key", null);
    const {show} = useModal();
    const {updateUser} = useMonitoring()

    const [details, setDetails] = useState<PremiumSubscriptionDetails | null>()

    function refresh(newLicenseKey = licenseKey ?? null) {
        fetchLicenseInfo(newLicenseKey).then(info => {
            setDetails(info)
            try {
                if (info.email) {
                    window.posthog.alias(info.email, window.posthog.get_distinct_id())
                }
            } catch (err) {
            }

            updateUser({licenseDetails: info})
        })
    }

    useEffect(() => {
        console.log("licenseKey changed ", licenseKey)
        if (!licenseKey) return;
        refresh(licenseKey)
    }, [licenseKey]);

    useEffect(() => {
        const queryParamLicenseKey = getQueryParam(window.location.href, "license_key");
        const utmSource = getQueryParam(window.location.href, "utm_source");

        if (queryParamLicenseKey) {
            removeQueryParam('license_key');
            if (utmSource) {
                removeQueryParam('utm_source');
            }
            setLicenseKey(queryParamLicenseKey);
            show({
                component: LicenseAppliedPopup,
                data: {licenseKey: queryParamLicenseKey, fromCheckout: utmSource === 'checkout', size: '2xl'},
            })
        }
    }, [])

    const data = useMemo(() => {
        return {
            hasPremium: !!licenseKey,
            details,
            licenseKey: licenseKey ?? null,
            refresh,
            setLicenseKey: (lk: string | null) => {
                setLicenseKey(lk);
                setDetails(null)
            },
        }
    }, [licenseKey, details]);

    return <PremiumContext.Provider value={data}>{children}</PremiumContext.Provider>
}

export const usePremium = (): PremiumContextType => {
    return useContext(PremiumContext) as PremiumContextType;
}
